import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { makeStyles } from "tss-react/mui";
import Select from "../../../base/ui/components/web/Select";
import { setSpokenLanguage } from "../../actions.any";
import { SUPPORTED_SPOKEN_LANGUAGES } from "../../constants";
import { getSpokenLanguage } from "../../functions";
// Import reducer to ensure it gets registered with ReducerRegistry
import "../../reducer";
/**
 * Styles for the SpokenLanguageSelector component.
 */
const useStyles = makeStyles()((theme) => {
    return {
        container: {
            display: "flex",
            alignItems: "center",
            padding: theme.spacing(2),
            gap: theme.spacing(2),
        },
        select: {
            flex: 1,
            minWidth: 150,
        },
        label: {
            ...theme.typography.bodyShortRegular,
            color: theme.palette.text01,
            whiteSpace: "nowrap",
        },
    };
});
/**
 * Component that renders a dropdown for selecting the user's spoken language.
 * This is used for live audio translation to identify the language the user speaks.
 *
 * @returns {JSX.Element} The rendered component.
 */
function SpokenLanguageSelector({ className }) {
    const { t } = useTranslation();
    const { classes, cx } = useStyles();
    const dispatch = useDispatch();
    const selectedLanguage = useSelector((state) => getSpokenLanguage(state));
    /**
     * Maps supported languages to Select component options format.
     * Includes a prompt placeholder only when no language is selected yet.
     * Once a language is chosen, only real language options are shown
     * (prevents deselection).
     */
    const languageOptions = [
        ...(!selectedLanguage
            ? [
                {
                    value: "",
                    label: t("translation.selectLanguagePlaceholder"),
                },
            ]
            : []),
        ...SUPPORTED_SPOKEN_LANGUAGES.map((lang) => ({
            value: lang,
            label: t(`translation.languages.${lang}`),
        })),
    ];
    /**
     * Handles language selection changes.
     * Ignores selection of the placeholder (empty value).
     */
    const onLanguageChange = useCallback((e) => {
        const value = e.target.value;
        if (value) {
            dispatch(setSpokenLanguage(value));
        }
    }, [dispatch]);
    return (React.createElement("div", { className: cx(classes.container, className) },
        React.createElement("span", { className: classes.label }, t("translation.spokenLanguageLabel")),
        React.createElement(Select, { className: classes.select, id: "spoken-language-select", onChange: onLanguageChange, options: languageOptions, value: selectedLanguage ?? "" })));
}
export default SpokenLanguageSelector;
